import { createFileRoute } from '@tanstack/react-router';
import { ArrowUpRightIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { useEffect } from 'react';

import { ClaudeIcon, CursorIcon, VSCodeIcon } from '@/components/Icons';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { LayoutHeaderTitle } from '@/components/LayoutHeaderTitle';
import { McpConfigTabs } from '@/components/MCP/McpConfigTabs';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { useUser } from '@/hooks/useUser';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/$organizationName/mcp')({
  component: MCPPage,
});

const EDITOR_LINKS = [
  {
    name: 'Claude Code',
    icon: ClaudeIcon,
    href: 'https://driverai.notion.site/Driver-MCP-Onboarding-for-Claude-Code-2648f239606f80488760ddd284907dbf',
    hoverColor: 'hover:border-[#D97757]',
  },
  {
    name: 'Cursor',
    icon: CursorIcon,
    href: 'https://driverai.notion.site/Driver-MCP-Onboarding-for-Cursor-2628f239606f8020bbd9d3e40a71d5a0',
    hoverColor: 'hover:border-[#14120b]',
  },
  {
    name: 'VS Code + Copilot',
    icon: VSCodeIcon,
    href: 'https://driverai.notion.site/Driver-MCP-Onboarding-for-VS-Code-Copilot-2648f239606f80ebbc7ce29ebad5d7b9',
    hoverColor: 'hover:border-[#007ACC]',
  },
] as const;

function MCPPage() {
  const { organizationDisplayName } = useUser();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - MCP Integration`;
  }, [organizationDisplayName]);

  return (
    <LayoutDefault
      navbarTitle="MCP Setup"
      header={
        <LayoutHeader>
          <LayoutHeaderTitle title="MCP Setup" />
        </LayoutHeader>
      }
    >
      <div className="mt-12">
        <SettingsPageLayout title="MCP Integration" subTitle="Supercharge your AI coding assistant with Driver">
          <div className="space-y-8">
            {/* Marketing Section */}
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Driver exposes powerful tools via the{' '}
                <span className="text-foreground font-medium">Model Context Protocol (MCP)</span> that LLM agents can
                use to access rich, pre-computed documentation about your codebase. This includes deep context docs for
                codebase-wide understanding and granular navigational tools for exploring specific files and symbols.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="border-border bg-muted/30 flex items-start gap-3 rounded-lg border p-4">
                  <div className="bg-driver-brand-blue/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                    <ZapIcon className="text-driver-brand-blue h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium">Faster Discovery</h3>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Reduce the work required for LLM agents to understand your codebase. No more missed context.
                    </p>
                  </div>
                </div>

                <div className="border-border bg-muted/30 flex items-start gap-3 rounded-lg border p-4">
                  <div className="bg-driver-brand-blue/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                    <SparklesIcon className="text-driver-brand-blue h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium">Improved Accuracy</h3>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Better coding agent performance with exhaustive, pre-computed context for large internal
                      codebases.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Connect Section */}
            <div className="space-y-4">
              <h2 className="text-sm font-medium">Connect to Driver&apos;s MCP server</h2>
              <McpConfigTabs />
            </div>

            {/* Detailed Setup Guides */}
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-medium">Detailed setup guides</h2>
                <p className="text-muted-foreground mt-1 text-xs">
                  Visit the full onboarding guide for your editor, including available tools and advanced configuration.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {EDITOR_LINKS.map((editor) => (
                  <a
                    key={editor.name}
                    href={editor.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${editor.name} MCP onboarding guide`}
                    className="group block"
                  >
                    <Card
                      className={cn(
                        'h-full cursor-pointer border-2 border-transparent transition-all duration-200',
                        'hover:shadow-md',
                        editor.hoverColor
                      )}
                    >
                      <CardContent className="flex flex-col items-center gap-4 p-6">
                        <div className="flex h-14 w-14 items-center justify-center rounded-xl">
                          <editor.icon className="h-[56px] w-[56px]" />
                        </div>

                        <div className="flex items-center gap-1.5 text-sm font-medium">
                          {editor.name}
                          <ArrowUpRightIcon className="text-muted-foreground h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </SettingsPageLayout>
      </div>
    </LayoutDefault>
  );
}
